import React from 'react';
import { BattleUnitState, TurnOrderEntry, HotbarSkill } from '../types/battle';
import { isUnitStunned } from '../utils/battleUtils';

interface BattleActionsBarProps {
  selectedUnit: BattleUnitState;
  currentTurnUnit: TurnOrderEntry | null;
  attackMode: boolean;
  skillMode: boolean;
  selectedSkill: HotbarSkill | null;
  equippedSkills: HotbarSkill[];
  onEnterAttackMode: () => void;
  onSelectSkillNode: (skill: HotbarSkill) => void;
  onShowWeaponRange: (unit: BattleUnitState) => void;
  onClearWeaponRange: () => void;
  onShowSkillRange: (skill: HotbarSkill) => void;
  onClearSkillRange: () => void;
}

const BattleActionsBar: React.FC<BattleActionsBarProps> = ({
  selectedUnit,
  currentTurnUnit,
  attackMode,
  skillMode,
  selectedSkill,
  equippedSkills,
  onEnterAttackMode,
  onSelectSkillNode,
  onShowWeaponRange,
  onClearWeaponRange,
  onShowSkillRange,
  onClearSkillRange,
}) => {
  const isCurrentTurn = !!currentTurnUnit && currentTurnUnit.unitId === selectedUnit.id;
  const weapon = selectedUnit.equipment?.weapon;
  const weaponAPCost = weapon?.weaponAPCost || 2;
  const weaponUnavailable = !weapon || selectedUnit.stats.currentAP < weaponAPCost;

  return (
    <div className={`actions-bar ${!isCurrentTurn ? 'not-current-turn' : ''}`}>
      {/* Slot 0 (backtick key): Attack */}
      <div
        className={`action-slot ${attackMode ? 'active' : ''} ${weaponUnavailable ? 'unavailable' : ''}`}
        onClick={() => {
          if (!isCurrentTurn) return;
          if (!weapon) return;
          if (selectedUnit.stats.currentAP < weaponAPCost) return;
          onEnterAttackMode();
        }}
        onMouseEnter={() => {
          if (weapon && !attackMode) onShowWeaponRange(selectedUnit);
        }}
        onMouseLeave={() => {
          if (!attackMode) onClearWeaponRange();
        }}
        title={weapon
          ? `Attack with ${weapon.itemName}\nRange: ${weapon.minRange}-${weapon.maxRange}\nDamage: ${weapon.minDamage}-${weapon.maxDamage}\nAP Cost: ${weaponAPCost}`
          : 'No weapon equipped'}
      >
        <div className="action-slot-number">`</div>
        <div className="action-slot-icon">⚔️</div>
        <div className="action-slot-name">Attack</div>
        {weapon && <div className="action-slot-cost">{weaponAPCost} AP</div>}
      </div>

      {/* Skills 0-9: Equipped Skills */}
      {Array.from({ length: 10 }, (_, index) => {
        const displayKey = index === 9 ? '0' : (index + 1).toString();
        const equippedSkill = equippedSkills.find(es => es.hotbarSlot === index);

        if (!equippedSkill) {
          return (
            <div key={index} className="action-slot empty" title="Empty slot - Configure in Character Skills screen">
              <div className="action-slot-number">{displayKey}</div>
              <div className="action-slot-empty-icon">+</div>
            </div>
          );
        }

        const skill = equippedSkill.skill;
        const cooldownInfo = selectedUnit.skillCooldowns?.find(
          (cd) => cd.skillNodeId === Number(skill.id)
        );
        const isOnCooldown = !!(cooldownInfo && cooldownInfo.turnsRemaining > 0);
        const stunned = isUnitStunned(selectedUnit);
        const canAfford = selectedUnit.stats.currentAP >= (skill.apCost || 0);
        const isUnavailable = !canAfford || isOnCooldown || stunned;
        const isActive = skillMode && !!selectedSkill && selectedSkill.skill.id === skill.id;

        return (
          <div
            key={index}
            className={`action-slot ${isActive ? 'active' : ''} ${isUnavailable ? 'unavailable' : ''}`}
            onClick={() => {
              if (!isCurrentTurn) return;
              if (isUnavailable) return;
              onSelectSkillNode(equippedSkill);
            }}
            onMouseEnter={() => {
              if (!isUnavailable && !skillMode) onShowSkillRange(equippedSkill);
            }}
            onMouseLeave={() => {
              if (!skillMode) onClearSkillRange();
            }}
            title={
              stunned ? 'Cannot use skills while stunned' :
              isOnCooldown ? `${skill.name}\n${skill.description}\n\nOn Cooldown: ${cooldownInfo!.turnsRemaining}/${cooldownInfo!.totalCooldown} turns` :
              `${skill.name}${skill.skillLevel ? ` (Level ${skill.skillLevel})` : ''}\n${skill.description}\n\nAP Cost: ${skill.apCost || 0}\nRange: ${skill.minRange || 0}-${skill.maxRange || 1}${skill.areaOfEffect > 0 ? `\nAOE: ${skill.areaOfEffect}` : ''}\nCooldown: ${skill.cooldown || 0} turns${skill.skillXP !== undefined && skill.xpToNextLevel ? `\n\nProgress: ${skill.skillXP}/${skill.xpToNextLevel} XP\nTimes Used: ${skill.timesUsed || 0}` : ''}`
            }
          >
            <div className="action-slot-number">{displayKey}</div>
            <div className="action-slot-icon">{skill.icon || '✨'}</div>
            <div className="action-slot-name">
              {skill.name}
              {skill.skillLevel && skill.skillLevel > 1 && (
                <span className="skill-level-badge">Lv{skill.skillLevel}</span>
              )}
            </div>
            <div className="action-slot-cost">{skill.apCost || 0} AP</div>
            {isOnCooldown && (
              <div className="action-slot-cooldown">{cooldownInfo!.turnsRemaining}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BattleActionsBar;
